/**
 * 构建信息工具。
 *
 * 字段由 rsbuild `source.define` 在构建时静态注入：
 *   - __BUILD_VERSION__：package.json 的 version
 *   - __BUILD_COMMIT__：git short commit（CI 环境优先取 env，否则现场 git rev-parse）
 *   - __BUILD_TIME__：构建时刻的 ISO 字符串（dev 模式即 dev server 启动时刻）
 *
 * 用途：在浏览器侧快速辨识"我现在看到的 console 是不是最新发的那一份"。
 * 打印形式刻意做得显眼（大字号 + 颜色），方便在一堆日志里一眼定位。
 */

export type BuildInfo = {
	version: string;
	commit: string;
	time: string;
};

/**
 * 单例构建信息。fallback 到 "unknown" 是为了即便 define 未注入也不抛错。
 */
export const buildInfo: BuildInfo = {
	version:
		typeof __BUILD_VERSION__ !== "undefined" ? __BUILD_VERSION__ : "unknown",
	commit:
		typeof __BUILD_COMMIT__ !== "undefined" ? __BUILD_COMMIT__ : "unknown",
	time: typeof __BUILD_TIME__ !== "undefined" ? __BUILD_TIME__ : "unknown",
};

/**
 * 在启动时打印一次构建信息。
 *
 * 同时把 buildInfo 挂到 `window.__CONSOLE_BUILD__`，方便后续手动在 DevTools 查询，
 * 也可被自动化脚本读取。
 */
export const printBuildInfo = () => {
	if (typeof window !== "undefined") {
		window.__CONSOLE_BUILD__ = buildInfo;
	}

	// 把 ISO 时间格式化成本地时区，更直观。
	let localTime = buildInfo.time;
	try {
		const d = new Date(buildInfo.time);
		if (!Number.isNaN(d.getTime())) {
			localTime = `${buildInfo.time}  (local ${d.toLocaleString()})`;
		}
	} catch {
		// 保留原字符串
	}

	const banner = `%c console %c v${buildInfo.version} %c ${buildInfo.commit} `;
	const labelStyle =
		"background:#111;color:#fff;padding:2px 6px;border-radius:3px 0 0 3px;font-weight:bold";
	const versionStyle =
		"background:#3b82f6;color:#fff;padding:2px 6px;font-weight:bold";
	const commitStyle =
		"background:#10b981;color:#fff;padding:2px 6px;border-radius:0 3px 3px 0;font-weight:bold";

	// 用 console.info 而非 console.log，避免在过滤"warnings/errors only"时被吞。
	console.info(banner, labelStyle, versionStyle, commitStyle);
	console.info(`%c build time: ${localTime}`, "color:#6b7280");
};
